import { axios, publicAxios, endpoints, createSSEConnection } from 'Api';

export const getAllActivePlans = async () => axios.post(endpoints.getAllActivePlans);

export const getUserDetailsFromCode = async ({ code }) => axios.post(endpoints.getUserDetFrmCode, { code });

export const getUserContents = async ({ content_limit, content_start, limit, start, user_id }) => axios.post(endpoints.getUserContents, { content_limit, content_start, limit, start, user_id });

export const addVideoDraftContent = async (body) => axios.post(endpoints.addVideoDraftContent, body);

export const addVideo = async (body) => axios.post(endpoints.addVideo, body);

export const addPhotoContent = async (body) => axios.post(endpoints.addPhotoContent, body);

export const addS3VideoDraftContent = async (body) => axios.post(endpoints.addS3VideoContent, body);

export const getVideosDetail = async ({ id }) => axios.post(endpoints.getVideosDetail, { id });

// Save the occasions a new normal user picks on the ChooseOccasion sign-up step.
// Authenticated call — the axios interceptor appends `token` automatically.
export const saveUserOccasions = async ({ occasions }) => axios.post(endpoints.saveUserOccasions, { occasions });

// Contribution APIs
export const listLinks = async ({ status = 'all' }) => axios.post(endpoints.listLinks, { status });

export const listContributorsByLink = async ({ uuid }) => axios.post(endpoints.listContributorsByLink, { uuid });

export const reactivateContributionLink = async ({ uuid }) => axios.post(endpoints.reactivateContributionLink, { uuid });

export const editContributionLink = async ({
  uuid, label, expires_at, timezone,
}) => axios.post(endpoints.editContributionLink, {
  uuid,
  ...(label !== undefined ? { label } : {}),
  ...(expires_at !== undefined ? { expires_at } : {}),
  ...(timezone !== undefined ? { timezone } : {}),
});

export const listContributors = async ({ group_by = 'email' }) => axios.post(endpoints.listContributors, { group_by });

// `uuid` is the folder (contribution link) uuid — always pass it so the query
// stays scoped to that folder even when filtering by email.
export const listContributions = async ({ filter_by = 'email', value, uuid }) => axios.post(endpoints.listContributions, { filter_by, value, ...(uuid ? { uuid } : {}) });

// Viewer / invite APIs (authenticated — the axios interceptor appends `token`).
// Look up whether an email already belongs to a Projector user before inviting.
export const searchUserToInvite = async ({ email }) => axios.post(endpoints.searchUserToInvite, { email });
// Invite one or more users as viewers. `data` is the array of viewer rows the
// BE expects (existing user → full row; not-on-Projector → email-only row).
export const addViewer = async ({ data }) => axios.post(endpoints.addViewer, { data });
// Current viewers and pending (invited but not yet accepted) viewers.
export const getViewersList = async () => axios.post(endpoints.getViewersList, {});
export const getPendingViewersList = async () => axios.post(endpoints.getPendingViewersList, {});

// Gift Flow APIs (using publicAxios - no token needed)
export const saveGiftUser = async ({ firstname, lastname, email, recaptchaResponse, note, v2, referral }) => publicAxios.post(endpoints.saveGiftUser, { firstname, lastname, email, recaptchaResponse, note, v2, ...(referral ? { referral } : {}) });

export const getAllGiftPlans = async () => publicAxios.post(endpoints.getAllActivePlans, { show_gift_plans: true });

export const createPaymentIntent = async ({ uid, plan_id, url, test, promo }) => publicAxios.post(endpoints.createPaymentIntent, { uid, plan_id, url, test, ...(promo ? { promo } : {}) });

export const registerGiftUser = async ({ uid, checkout_id, plan_id, fromName, fromEmail, cardholderName, cardholderEmail, deliveryDate, promo_code, v2 }) => publicAxios.post(endpoints.registerGiftUser, { uid, checkout_id, plan_id, fromName, fromEmail, cardholderName, cardholderEmail, deliveryDate, promo_code, v2 });

export const updateGiftUser = async ({ uid, email, note, contributor_details, fromName, fromEmail, phone, completed_steps, resume_data }) => publicAxios.post(endpoints.updateGiftUser, { uid, ...(email ? { email } : {}), ...(note ? { note } : {}), ...(contributor_details ? { contributor_details } : {}), ...(fromName ? { fromName } : {}), ...(fromEmail ? { fromEmail } : {}), ...(phone ? { phone } : {}), ...(completed_steps ? { completed_steps } : {}), ...(resume_data !== undefined ? { resume_data } : {}) });

// Store pending gift action for existing users (to be executed later)
export const addGiftAction = async ({ gift_uuid, user_id, action }) => publicAxios.post(endpoints.addGiftAction, { gift_uuid, user_id, action });

// Get pending gift actions for existing users
export const getGiftPendingActions = async (gift_uuid) => publicAxios.get(`${endpoints.getGiftPendingActions}?gift_uuid=${gift_uuid}`);

// Clear/complete all pending gift actions
export const clearGiftActions = async ({ gift_uuid, mark_completed }) => publicAxios.post(endpoints.clearGiftActions, { gift_uuid, mark_completed });

// Get gift status (polling/initial fetch)
export const getGiftStatus = async (uid) => publicAxios.get(`/getGiftStatus?uid=${uid}`);

// Send gift email to recipient
export const sendGiftEmail = async ({ uuid }) => publicAxios.post(endpoints.sendGiftEmail, { uuid });

// Send gift link to phone via SMS
export const sendGiftLinkToPhone = async ({ mobile, uid, token, update_profile = false }) => publicAxios.post(endpoints.sendGiftLinkToPhone, { mobile, uid, token, update_profile });

// Batch send the gift-status share URL to multiple recipients (mix of
// phone numbers and emails). Used by ContactShareModal on the gift status
// page after the user picks contacts via the native Web Contacts API or
// enters one manually. BE owns the actual SMS/email dispatch.
//
// Payload shape:
//   {
//     url: 'https://0.eq7.in/gift?share=...',
//     message: 'Add a memory to my Projector gift',
//     recipients: [
//       { name?: 'Charlie', phone?: '+15551234567', email?: 'c@x.com' },
//       ...
//     ]
//   }
// Each recipient must have at least one of phone or email; BE fans out
// accordingly. Returns { sent, failed, errors? }.
export const sendGiftLinkBatch = async ({ url, message, recipients }) => publicAxios.post(endpoints.sendGiftLinkBatch, { url, message, recipients });

// Preview gift email HTML (for Send Yourself / copy to clipboard)
export const previewGiftEmail = async ({ uid, token }) => publicAxios.post(endpoints.previewGiftEmail, { uid, token });

// Contribution APIs
export const createContributionLink = async ({ expires_at }) => axios.post(endpoints.createContributionLink, { expires_at });

export const getContributionLinkInfo = async ({ temp_token }) => publicAxios.post(endpoints.getContributionLinkInfo, { temp_token });

export const submitContribution = async ({
  uuid, temp_token, email, name, type, payload,
}) => publicAxios.post(endpoints.submitContribution, {
  uuid, temp_token, email, name, type, payload,
});

// Public — list the returning contributor's own uploads for this link.
// Scoped server-side by temp_token → link and filtered by email/phone.
// Response: { success, total, contributions: [{ id, type, title,
//   thumbnail_signed, created_at, is_published, connected_source }] }
export const listMyContributions = async ({ temp_token, email }) => publicAxios.post(
  endpoints.listMyContributions, { temp_token, email },
);

// Public — delete one of the contributor's OWN, not-yet-published uploads.
// Server rejects with 409 { reason: 'already_published' } if published.
export const deleteMyContribution = async ({ temp_token, email, contribution_id }) => publicAxios.post(
  endpoints.deleteMyContribution, { temp_token, email, contribution_id },
);

// Public — the link owner's plan + how many items they've already uploaded.
// Response: { success, total_content_uploaded, subscription: {...}|null }
// subscription null or subscription.id === '6' (Free) → 15-item cap; every
// other plan is unlimited.
export const getContributionSubscription = async ({ temp_token, uuid }) => publicAxios.post(
  endpoints.getContributionSubscription, { temp_token, uuid },
);

export const createShortLink = async ({ token, path }) => {
  const res = await publicAxios.post(endpoints.shortLink, { token, path });
  if (res?.data?.link) {
    const { origin } = window.location;
    // Replace production domain with current environment domain for non-prod
    if (origin.includes('localhost') || origin.includes('preprod') || origin.includes('dev.app')) {
      const url = new URL(res.data.link);
      res.data.link = `${origin}${url.pathname}`;
    }
  }
  return res;
};

// Update gift status for mobile flow - pushes real-time messages via SSE
export const updateGiftStatus = async ({ uuid, push, token, uploaderToken, mobileUpload }) => publicAxios.post(endpoints.updateGiftStatus, { uuid, push, token, uploaderToken, mobileUpload });

// Helper to parse SSE push field (shared between initial connection and reconnects)
const parseSSEMessage = (event, onMessage) => {
  // console.log('SSE raw message:', event.data);
  try {
    const data = JSON.parse(event.data);

    // Parse the push field if it exists
    if (data.push && typeof data.push === 'string') {
      try {
        // The push field is a JSON-like string, but not valid JSON (uses single quotes and unquoted keys)
        // Convert it to valid JSON format first
        const jsonString = data.push
          .replace(/(\w+):/g, '"$1":') // Add quotes around keys
          .replace(/:\s*'((?:[^'\\]|\\.)*)'/g, (match, p1) => {
            const unescaped = p1.replace(/\\'/g, "'");
            const escaped = unescaped.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
            return `:"${escaped}"`;
          })
          .replace(/\['/g, '["') // Replace [' with ["
          .replace(/'\]/g, '"]') // Replace '] with "]
          .replace(/','/g, '","'); // Replace ',' with ","

        const pushData = JSON.parse(jsonString);
        data.parsedPush = pushData;
        // console.log('Parsed push data:', pushData);
      } catch (pushError) {
        console.error('Error parsing push field:', pushError);
        data.parsedPush = null;
      }
    }

    if (onMessage) onMessage(data);
  } catch (err) {
    console.error('Error parsing SSE message:', err);
    if (onMessage) onMessage(event.data); // Pass raw data if not JSON
  }
};

// Gift Flow SSE - Listen to real-time upload status updates (with auto-reconnect)
// Proactively reconnects every 50s to avoid server's 60s timeout
export const subscribeToGiftUploadStatus = (uid, { onMessage, onError, onOpen }) => {
  let eventSource = null;
  let isClosed = false;
  let reconnectAttempts = 0;
  let reconnectTimer = null;
  let proactiveRefreshTimer = null;
  const MAX_RECONNECT_DELAY = 30000; // Cap at 30s
  const BASE_RECONNECT_DELAY = 1000; // Start at 1s
  const PROACTIVE_REFRESH_INTERVAL = 50000; // Refresh every 50s (before 60s server timeout)

  const connect = () => {
    if (isClosed) return;

    // Clear any existing proactive refresh timer
    if (proactiveRefreshTimer) {
      clearTimeout(proactiveRefreshTimer);
      proactiveRefreshTimer = null;
    }

    eventSource = createSSEConnection('/giftUploadStatus', { uid });

    eventSource.onopen = () => {
      // console.log('[SSE] Connection opened (attempt:', reconnectAttempts, ')');
      reconnectAttempts = 0; // Reset on successful connection
      if (onOpen) onOpen();

      // Schedule proactive refresh before server timeout
      proactiveRefreshTimer = setTimeout(() => {
        if (isClosed) return;
        // console.log('[SSE] Proactive refresh - reconnecting before timeout');
        if (eventSource) {
          eventSource.close();
        }
        connect();
      }, PROACTIVE_REFRESH_INTERVAL);
    };

    eventSource.onmessage = (event) => parseSSEMessage(event, onMessage);

    eventSource.onerror = (error) => {
      console.error('[SSE] Connection error:', error);
      if (onError) onError(error);

      if (isClosed) return;

      // Clear proactive refresh since we're reconnecting due to error
      if (proactiveRefreshTimer) {
        clearTimeout(proactiveRefreshTimer);
        proactiveRefreshTimer = null;
      }

      // Close the broken connection
      eventSource.close();

      // Reconnect with exponential backoff (1s, 2s, 4s, 8s, ... capped at 30s)
      reconnectAttempts += 1;
      const delay = Math.min(BASE_RECONNECT_DELAY * (2 ** (reconnectAttempts - 1)), MAX_RECONNECT_DELAY);
      // console.log(`[SSE] Reconnecting in ${delay}ms (attempt ${reconnectAttempts})`);
      reconnectTimer = setTimeout(connect, delay);
    };
  };

  connect();

  // Return cleanup function to close connection and stop reconnecting
  return () => {
    isClosed = true;
    if (reconnectTimer) clearTimeout(reconnectTimer);
    if (proactiveRefreshTimer) clearTimeout(proactiveRefreshTimer);
    if (eventSource) eventSource.close();
  };
};

// ============================================================
// Mobile Upload SSE (for "Upload From Phone" in main app)
// ============================================================

// Create a mobile upload SSE session — returns { session_id }
export const createUploadSSESession = async () => {
  console.log('[MobileUpload API] Creating SSE session...');
  const response = await axios.post(endpoints.createUploadSSESession);
  console.log('[MobileUpload API] SSE session created:', response?.data);
  return response;
};

// Push status update from mobile to desktop via SSE channel
export const updateUploadStatus = async ({ session_id, status_data }) => {
  console.log('[MobileUpload API] Sending status update | session:', session_id, '| data:', status_data);
  const response = await axios.post(endpoints.updateUploadStatus, { session_id, status_data });
  console.log('[MobileUpload API] Status update sent successfully');
  return response;
};

// Public variants for contribute flow (no auth token needed)
export const createUploadSSESessionPublic = async (token) => {
  console.log('[Contribute SSE] Creating public SSE session...');
  const response = await publicAxios.post(endpoints.createUploadSSESession, { token });
  console.log('[Contribute SSE] Public SSE session created:', response?.data);
  return response;
};

export const updateUploadStatusPublic = async ({ session_id, status_data, token }) => {
  console.log('[Contribute SSE] Sending public status update | session:', session_id);
  const response = await publicAxios.post(endpoints.updateUploadStatus, { session_id, status_data, token });
  return response;
};

// Subscribe to mobile upload status stream (same reconnect pattern as gift flow)
// Proactively reconnects every 55s to avoid server's 60s timeout
export const subscribeToUploadStatus = (session_id, token, { onMessage, onError, onOpen }) => {
  let eventSource = null;
  let isClosed = false;
  let reconnectAttempts = 0;
  let reconnectTimer = null;
  let proactiveRefreshTimer = null;
  const MAX_RECONNECT_DELAY = 30000;
  const BASE_RECONNECT_DELAY = 1000;
  const PROACTIVE_REFRESH_INTERVAL = 55000; // 55s (before 60s server timeout)

  const connect = () => {
    if (isClosed) return;

    if (proactiveRefreshTimer) {
      clearTimeout(proactiveRefreshTimer);
      proactiveRefreshTimer = null;
    }

    console.log('[MobileUpload SSE] Opening EventSource connection | session:', session_id);
    eventSource = createSSEConnection(endpoints.uploadStatusStream, { session_id, token });

    eventSource.onopen = () => {
      console.log('[MobileUpload SSE] ✓ Connection opened | session:', session_id, '| attempt:', reconnectAttempts);
      reconnectAttempts = 0;
      if (onOpen) onOpen();

      proactiveRefreshTimer = setTimeout(() => {
        if (isClosed) return;
        console.log('[MobileUpload SSE] Proactive refresh - reconnecting before timeout');
        if (eventSource) {
          eventSource.close();
        }
        connect();
      }, PROACTIVE_REFRESH_INTERVAL);
    };

    eventSource.onmessage = (event) => {
      console.log('[MobileUpload SSE] raw message:', event.data);
      try {
        const data = JSON.parse(event.data);
        if (data.status_data && typeof data.status_data === 'string') {
          try {
            data.parsedStatusData = JSON.parse(data.status_data);
          } catch (e) {
            console.error('[MobileUpload SSE] Error parsing status_data:', e);
            data.parsedStatusData = null;
          }
        }
        if (onMessage) onMessage(data);
      } catch (err) {
        console.error('[MobileUpload SSE] Error parsing message:', err);
        if (onMessage) onMessage(event.data);
      }
    };

    eventSource.onerror = (error) => {
      console.error('[MobileUpload SSE] Connection error:', error);
      console.error('[MobileUpload SSE] EventSource readyState:', eventSource.readyState, '(0=CONNECTING, 1=OPEN, 2=CLOSED)');
      console.error('[MobileUpload SSE] EventSource url:', eventSource.url);
      if (onError) onError(error);

      if (isClosed) return;

      if (proactiveRefreshTimer) {
        clearTimeout(proactiveRefreshTimer);
        proactiveRefreshTimer = null;
      }

      eventSource.close();

      reconnectAttempts += 1;
      const delay = Math.min(BASE_RECONNECT_DELAY * (2 ** (reconnectAttempts - 1)), MAX_RECONNECT_DELAY);
      console.log(`[MobileUpload SSE] Reconnecting in ${delay}ms (attempt ${reconnectAttempts})`);
      reconnectTimer = setTimeout(connect, delay);
    };
  };

  console.log('[MobileUpload SSE] Initiating connection | session:', session_id);
  connect();

  return () => {
    console.log('[MobileUpload SSE] Cleanup called — closing connection | session:', session_id);
    isClosed = true;
    if (reconnectTimer) clearTimeout(reconnectTimer);
    if (proactiveRefreshTimer) clearTimeout(proactiveRefreshTimer);
    if (eventSource) eventSource.close();
  };
};
